import resolveIdentityTint from '@Guidelines/Theme/helpers/resolveIdentityTint';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import type * as React from 'react';

type Size = 'small' | 'large';

const DIMENSIONS_BY_SIZE: Record<Size, { size: string; radius: string; fontSize: string }> = {
	small: { size: '2.375rem', radius: '12px', fontSize: '0.8125rem' },
	large: { size: '3.5rem', radius: '16px', fontSize: '1.125rem' },
};

const tileStyle = (solid: string, size: Size) => {
	const dimensions = DIMENSIONS_BY_SIZE[size];

	return css`
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		width: ${dimensions.size};
		height: ${dimensions.size};
		border-radius: ${dimensions.radius};
		background-color: ${solid};
		color: ${theme.colors.surface};
		font-family: ${theme.fonts.display};
		font-weight: ${theme.fontWeights.semibold};
		font-size: ${dimensions.fontSize};
	`;
};

type Props = {
	/** Short text drawn inside the tile — typically a person's initials. */
	readonly label: string;
	/**
	 * Deterministic seed choosing the tint among the four identity pairs of `theme.tints` (a database
	 * id works well): the same seed always resolves to the same tint, so the same identity keeps its
	 * color across reloads.
	 */
	readonly seed: number;
	/**
	 * Tile footprint. `small` (default) suits a dense row or card. `large` suits a record header,
	 * where the tile stands as the dominant identity marker.
	 */
	readonly size?: Size;
};

/**
 * Generic identity marker — a solid-filled square carrying a short label. It knows nothing about the
 * entity it represents: the caller resolves the label (initials, typically). Purely decorative — the
 * caller always renders the full identity as adjacent text, so the tile carries `aria-hidden` rather
 * than duplicate that name for assistive technology. The tint always comes from `theme.tints`, never
 * `theme.states`: a tint distinguishes an identity, a state pair carries a meaning about a condition
 * (`reference/design-direction.md` §4).
 */
const IdentityTile: React.FC<Props> = ({ label, seed, size = 'small' }) => {
	const tint = resolveIdentityTint(seed);

	return (
		<div css={tileStyle(tint.solid, size)} aria-hidden="true">
			{label}
		</div>
	);
};

export default IdentityTile;
